<script setup lang="ts">
  import type { Agency } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    Button,
    FormField,
    FormSubmit,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { agencyKey } from '@gat-ship/types/injection-keys'
  import { inject } from 'vue'
  import { CompanyPicker } from '@gat-ship/ref-data'

  const isOpen = defineModel<boolean>('open', { default: false })
  const agencyProvider = inject(agencyKey)
  const description = ref('')

  const linkAgencySchema = z.object({
    agencyId: z.string().min(1),
    companyCode: z.string().min(1, 'Please select a company')
  })
  type LinkAgencyFormValues = z.infer<typeof linkAgencySchema>

  const form = useForm({
    defaultValues: {
      agencyId: '',
      companyCode: ''
    } as LinkAgencyFormValues,
    validators: {
      onChange: linkAgencySchema
    },
    onSubmit: async ({ value }) => {
      await agencyProvider?.commands.linkToCompany(value.agencyId, {
        companyCode: value.companyCode
      })
      isOpen.value = false
    }
  })

  export interface ShowLinkToOtherCompanyModalParams {
    agency: Agency
  }

  function showModal({ agency }: ShowLinkToOtherCompanyModalParams) {
    description.value = agency.name
    form.reset({
      agencyId: agency.id,
      companyCode: agency.linkedCompany?.code ?? ''
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle> Link To Other Company </ModalHeaderTitle>
      <ModalHeaderDescription v-if="description">
        {{ description }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FormField
          :form="form"
          name="companyCode"
          label="Company"
          hint="The legally bound company entity."
          required
        >
          <template #default="{ field, invalid }">
            <CompanyPicker
              :id="field.name"
              :model-value="field.state.value"
              :aria-invalid="invalid"
              :data-testid="testIds.common.select.company"
              @update:model-value="field.handleChange"
              @blur="field.handleBlur"
            />
          </template>
        </FormField>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Link" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
